import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { db, storage } from '../../firebase';
import { doc, getDoc, collection, query, where, getDocs } from 'firebase/firestore';
import { ref, getDownloadURL } from 'firebase/storage';
import { FaArrowLeft, FaUser, FaMapMarkerAlt, FaEnvelope, FaPhone, FaCalendar } from 'react-icons/fa';
import targetBg from '../../target_bg_profil.png';

const ClubDetailsPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [club, setClub] = useState(null);
  const [competitions, setCompetitions] = useState([]);
  const [followersCount, setFollowersCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const scrollRef = React.useRef(null);

  const scrollRight = () => {
    if (scrollRef.current) {
      scrollRef.current.scrollBy({ left: 280, behavior: 'smooth' });
    }
  };

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);

    const fetchClubData = async () => {
      setLoading(true);
      try {
        const docRef = doc(db, 'Clubs', id);
        const docSnap = await getDoc(docRef);

        if (docSnap.exists()) {
          const data = docSnap.data();
          const clubData = {
            id: docSnap.id,
            name: data.clubName || 'Névtelen egyesület',
            location: data.location || 'Ismeretlen helyszín',
            imageUrl: data.profileImageUrl || data.logoUrl || '',
            ...data
          };

          if (!clubData.imageUrl) {
            try {
              const logoRef = ref(storage, `club_logos/${id}`);
              clubData.imageUrl = await getDownloadURL(logoRef);
            } catch (e) {
              // Stay with default
            }
          }
          setClub(clubData);

          // Lekérdezzük a klub által szervezett aktív versenyeket (kiszűrve a rejtett elemeket)
          const q = query(collection(db, 'events'), where('organizerId', '==', id));
          const compSnap = await getDocs(q);
          const compList = [];
          compSnap.forEach(doc => {
            const data = doc.data();
            const isHidden = data.isHidden === true;
            const rawName = data.competitionName || data.title || "";
            const isHiddenInName = rawName.includes("[REJTETT]");
            if (!isHidden && !isHiddenInName) {
              compList.push({ id: doc.id, ...data });
            }
          });
          compList.sort((a, b) => (a.competitionDate || '').localeCompare(b.competitionDate || ''));
          setCompetitions(compList);

          try {
            const followersSnap = await getDocs(collection(db, 'Clubs', id, 'followers'));
            setFollowersCount(followersSnap.size);
          } catch (e) {
            setFollowersCount(0);
          }
        } else {
          navigate('/egyesuletek');
        }
      } catch (error) {
        console.error("Error fetching club details:", error);
      }
      setLoading(false);
    };

    fetchClubData();
    return () => window.removeEventListener('resize', handleResize);
  }, [id, navigate]);

  if (loading) {
    return <div style={centerTextStyle}>Betöltés... 🏹</div>;
  }

  if (!club) return null;

  return (
    <div style={pageContainerStyle}>
      {/* Background & Header Section (Responsive Wrapper) */}
      <div style={headerContainerStyle}>
        <img
          src={targetBg}
          alt="Background"
          style={backgroundImageStyle}
        />
        <div style={overlayStyle} />

        <div style={profileHeaderOverlayStyle}>
          <div style={textContainerStyle}>
            <h1 style={clubNameStyle}>{club.name}</h1>
            <p style={clubLocationStyle}>{club.location}</p>
          </div>
          <div style={logoContainerStyle}>
            <img
              src={club.imageUrl || '/profil_def_v1.png'}
              alt="Club Logo"
              style={profileLogoStyle}
            />
          </div>
        </div>
      </div>

      {/* Main Info Card */}
      <div style={contentContainerStyle}>
        <div style={infoCardStyle}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
            <h3 style={{ ...cardTitleStyle, margin: 0 }}>Információk</h3>
            <span style={{ fontSize: '0.9rem', color: '#666', fontWeight: 'bold' }}>Követők: <span style={{ color: '#b30000' }}>{followersCount}</span></span>
          </div>

          {club.contactPerson && (
            <div style={infoItemStyle}>
              <FaUser style={iconStyle} />
              <div style={infoTextStyle}>{club.contactPerson}</div>
            </div>
          )}

          {(club.address || club.country) && (
            <div style={infoItemStyle}>
              <FaMapMarkerAlt style={iconStyle} />
              <div style={infoTextStyle}>
                {club.country && `${club.country}, `}{club.address}
              </div>
            </div>
          )}

          {club.email && (
            <div style={infoItemStyle}>
              <FaEnvelope style={iconStyle} />
              <div style={infoTextStyle}>{club.email}</div>
            </div>
          )}

          {club.phone && (
            <div style={infoItemStyle}>
              <FaPhone style={iconStyle} />
              <div style={infoTextStyle}>{club.phone}</div>
            </div>
          )}
        </div>

        {/* --- MEGSZERVEZETT VERSENYEK SZEKCIÓ --- */}
        {competitions.length > 0 && (
          <div style={{ marginTop: '25px' }}>
            <h3 style={{ ...cardTitleStyle, marginBottom: '12px' }}>Meghirdetett versenyek</h3>

            {isMobile && competitions.length > 1 ? (
              <div style={{ position: 'relative' }}>
                <div ref={scrollRef} style={horizontalScrollContainer}>
                  {competitions.map(comp => (
                    <div
                      key={comp.id}
                      onClick={() => navigate(`/verseny/${comp.id}`)}
                      style={compCardStyle(isMobile)}
                    >
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px' }}>
                        <h4 style={{ margin: 0, fontSize: '1rem', color: '#b30000', fontWeight: 'bold', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {comp.competitionName || comp.title}
                        </h4>
                        <span style={{ fontSize: '0.75rem', backgroundColor: '#fff5f5', color: '#b30000', padding: '2px 8px', borderRadius: '10px', fontWeight: 'bold', border: '1px solid #feb2b2', textTransform: 'uppercase' }}>
                          {comp.competitionType || '3D'}
                        </span>
                      </div>
                      <div style={{ fontSize: '0.85rem', color: '#555', display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '4px' }}>
                        <FaCalendar size={12} color="#b30000" /> {comp.competitionDate || 'Nincs dátum'}
                      </div>
                      <div style={{ fontSize: '0.85rem', color: '#555', display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <FaMapMarkerAlt size={12} color="#b30000" /> {comp.location || 'Helyszín'}
                      </div>
                    </div>
                  ))}
                </div>
                <button
                  onClick={scrollRight}
                  style={scrollArrowBtnStyle}
                  title="Tovább"
                >
                  ❯
                </button>
              </div>
            ) : (
              <div style={isMobile ? horizontalScrollContainer : desktopGridContainer}>
                {competitions.map(comp => (
                  <div
                    key={comp.id}
                    onClick={() => navigate(`/verseny/${comp.id}`)}
                    style={compCardStyle(isMobile)}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px' }}>
                      <h4 style={{ margin: 0, fontSize: '1rem', color: '#b30000', fontWeight: 'bold', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {comp.competitionName || comp.title}
                      </h4>
                      <span style={{ fontSize: '0.75rem', backgroundColor: '#fff5f5', color: '#b30000', padding: '2px 8px', borderRadius: '10px', fontWeight: 'bold', border: '1px solid #feb2b2', textTransform: 'uppercase' }}>
                        {comp.competitionType || '3D'}
                      </span>
                    </div>
                    <div style={{ fontSize: '0.85rem', color: '#555', display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '4px' }}>
                      <FaCalendar size={12} color="#b30000" /> {comp.competitionDate || 'Nincs dátum'}
                    </div>
                    <div style={{ fontSize: '0.85rem', color: '#555', display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <FaMapMarkerAlt size={12} color="#b30000" /> {comp.location || 'Helyszín'}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Back Button */}
        <button onClick={() => navigate(-1)} style={backButtonStyle}>
          VISSZA
        </button>
      </div>
    </div>
  );
};

// --- STYLES ---
const pageContainerStyle = {
  backgroundColor: '#fdfdfd',
  minHeight: '100vh',
  paddingBottom: '80px',
  position: 'relative',
  overflowX: 'hidden'
};

const headerContainerStyle = {
  width: '100%',
  minHeight: '220px',
  position: 'relative',
  overflow: 'hidden',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'flex-end',
  padding: '24px 16px 20px 16px',
  boxSizing: 'border-box'
};

const backgroundImageStyle = {
  position: 'absolute',
  top: 0,
  left: 0,
  width: '100%',
  height: '100%',
  objectFit: 'cover',
  zIndex: 1
};

const overlayStyle = {
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: 'rgba(0, 0, 0, 0.55)',
  zIndex: 2
};

const profileHeaderOverlayStyle = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'flex-end',
  position: 'relative',
  zIndex: 3,
  width: '100%',
  gap: '12px',
  boxSizing: 'border-box'
};

const textContainerStyle = {
  flex: 1,
  paddingRight: '8px',
  minWidth: 0,
  boxSizing: 'border-box'
};

const clubNameStyle = {
  fontSize: '19px',
  fontWeight: 'bold',
  color: 'white',
  margin: 0,
  textShadow: '2px 2px 4px rgba(0,0,0,0.8)',
  lineHeight: '1.2',
  wordBreak: 'break-word'
};

const clubLocationStyle = {
  fontSize: '13px',
  fontStyle: 'italic',
  color: '#e0e0e0',
  margin: '4px 0 0 0',
  textShadow: '1px 1px 2px rgba(0,0,0,0.8)',
  wordBreak: 'break-word'
};

const logoContainerStyle = {
  width: '80px',
  height: '80px',
  borderRadius: '50%',
  backgroundColor: 'white',
  padding: '3px',
  boxShadow: '0 4px 8px rgba(0,0,0,0.3)',
  flexShrink: 0,
  boxSizing: 'border-box'
};

const profileLogoStyle = {
  width: '100%',
  height: '100%',
  borderRadius: '50%',
  objectFit: 'cover'
};

const contentContainerStyle = {
  padding: '16px'
};

const infoCardStyle = {
  backgroundColor: 'white',
  borderRadius: '12px',
  padding: '20px',
  boxShadow: '0 2px 10px rgba(0,0,0,0.08)',
  marginTop: '20px'
};

const cardTitleStyle = {
  fontSize: '18px',
  fontWeight: 'bold',
  color: '#D32F2F',
  margin: '0 0 16px 0'
};

const infoItemStyle = {
  display: 'flex',
  alignItems: 'center',
  marginBottom: '14px'
};

const iconStyle = {
  color: '#D32F2F',
  fontSize: '18px',
  width: '24px'
};

const infoTextStyle = {
  fontSize: '15px',
  color: '#333',
  marginLeft: '12px'
};

const horizontalScrollContainer = {
  display: 'flex',
  gap: '15px',
  overflowX: 'auto',
  paddingBottom: '10px',
  scrollSnapType: 'x mandatory'
};

const scrollArrowBtnStyle = {
  position: 'absolute',
  right: '4px',
  top: '50%',
  transform: 'translateY(-50%)',
  width: '36px',
  height: '36px',
  borderRadius: '50%',
  backgroundColor: 'rgba(179, 0, 0, 0.85)',
  color: 'white',
  border: '2px solid white',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
  boxShadow: '0 4px 10px rgba(0,0,0,0.3)',
  zIndex: 10,
  fontSize: '18px',
  fontWeight: 'bold',
  paddingBottom: '2px'
};

const desktopGridContainer = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
  gap: '15px'
};

const compCardStyle = (isMobile) => ({
  backgroundColor: 'white',
  borderRadius: '12px',
  padding: '16px',
  boxShadow: '0 4px 12px rgba(0,0,0,0.06)',
  border: '1px solid #eee',
  cursor: 'pointer',
  minWidth: isMobile ? '280px' : 'auto',
  scrollSnapAlign: 'start',
  transition: '0.2s'
});

const backButtonStyle = {
  width: '100%',
  marginTop: '24px',
  padding: '12px',
  backgroundColor: 'transparent',
  border: 'none',
  color: '#757575',
  fontWeight: 'bold',
  fontSize: '14px',
  cursor: 'pointer'
};

const centerTextStyle = {
  textAlign: 'center',
  padding: '100px 20px',
  color: '#757575',
  fontFamily: 'Arial'
};

export default ClubDetailsPage;
